/** Dünne Schicht über der API. Kennt keine Krypto — die läuft eine Ebene höher. */

export type Session = {
  token: string;
  accountId: string;
  deviceId: string;
  handle: string;
};

const SESSION_KEY = 'wm.session';

export function loadSession(): Session | null {
  const raw = localStorage.getItem(SESSION_KEY);
  return raw ? (JSON.parse(raw) as Session) : null;
}

export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Grund einer unfreiwilligen Abmeldung, damit die Anmeldeseite ihn zeigen kann.
 *
 * In sessionStorage, nicht localStorage: Der Hinweis gilt fuer genau diesen
 * Moment. Ihn ueber Wochen aufzuheben hiesse, ihn beim naechsten Oeffnen
 * erneut anzuzeigen, ohne dass etwas passiert waere.
 */
const REASON_KEY = 'wm.logoutReason';

export function setLogoutReason(reason: string): void {
  try {
    sessionStorage.setItem(REASON_KEY, reason);
  } catch {
    // Privates Fenster oder blockierter Speicher — dann eben ohne Hinweis.
  }
}

/** Liest den Grund und verbraucht ihn: Er soll genau einmal erscheinen. */
export function takeLogoutReason(): string | null {
  try {
    const reason = sessionStorage.getItem(REASON_KEY);
    if (reason) sessionStorage.removeItem(REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}

/**
 * Adresse des API-Servers.
 *
 * Im Browser leer: Die App wird vom selben Ursprung ausgeliefert wie die API,
 * relative Pfade treffen also von selbst. In der Android-App ist der Ursprung
 * `http://localhost` — dort zeigte `/v1/...` auf die App selbst, wo nichts
 * liegt. Deshalb wird die Adresse beim Bauen gesetzt:
 *
 *   VITE_API_BASE=https://messenger.odarchenko.de npm run build
 *
 * Bewusst zur BAUZEIT und nicht zur Laufzeit konfigurierbar: Eine Adresse, die
 * sich nachtraeglich aendern laesst, waere ein Weg, einen Client auf einen
 * fremden Server umzulenken.
 */
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

/** Aus einem Pfad wie "/v1/messages" die vollstaendige Adresse machen. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const res = await fetch(apiUrl(path), {
    ...init,
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export type DirectoryEntry = { account_id: string; handle: string; device_id: string };

/**
 * Ein Kontakt ist eine PERSON, kein Gerät.
 *
 * `/v1/directory` liefert eine Zeile je Gerät. Wer zwei Geräte eingerichtet
 * hat, stünde sonst zweimal gleichnamig in der Liste, und eine Nachricht
 * erreichte nur eines davon — das andere bliebe stumm. Siehe
 * docs/01-architektur.md §6: Eine Nachricht an einen Nutzer mit drei Geräten
 * sind drei Verschlüsselungen.
 */
export type Contact = { accountId: string; handle: string; deviceIds: string[] };

/**
 * Verzeichniszeilen zu Kontakten zusammenfassen.
 *
 * Gefiltert wird das EIGENE KONTO, nicht nur das eigene Gerät: Sonst taucht
 * ein früheres eigenes Gerät als vermeintlich fremder Kontakt mit dem eigenen
 * Namen auf.
 */
export function groupDirectory(entries: DirectoryEntry[], ownAccountId: string): Contact[] {
  const byAccount = new Map<string, Contact>();

  for (const entry of entries) {
    if (entry.account_id === ownAccountId) continue;
    const existing = byAccount.get(entry.account_id);
    if (existing) existing.deviceIds.push(entry.device_id);
    else
      byAccount.set(entry.account_id, {
        accountId: entry.account_id,
        handle: entry.handle,
        deviceIds: [entry.device_id],
      });
  }

  return [...byAccount.values()].sort((a, b) => a.handle.localeCompare(b.handle));
}

export const api = {
  /**
   * Anmeldung mit Name und Passwort. Gibt ein Token zurück, das NUR zur
   * Geräteregistrierung und Zustellung berechtigt — alte Nachrichten bleiben
   * unlesbar, weil deren Schlüssel nur auf dem ursprünglichen Gerät liegen.
   */
  login: (handle: string, password: string) =>
    request<{
      accountId: string;
      handle: string;
      mustChangePassword: boolean;
      token: string;
    }>('/v1/login', { method: 'POST', body: JSON.stringify({ handle, password }) }),

  changePassword: (handle: string, currentPassword: string, newPassword: string) =>
    request<void>('/v1/password', {
      method: 'POST',
      body: JSON.stringify({ handle, currentPassword, newPassword }),
    }),

  /** Gerät mit lokal erzeugten Schlüsseln registrieren. */
  registerDevice: (token: string, device: unknown) =>
    request<{ accountId: string; deviceId: string; token: string }>(
      '/v1/devices',
      { method: 'POST', body: JSON.stringify(device) },
      token,
    ),

  directory: (token: string) =>
    request<{ users: DirectoryEntry[] }>('/v1/directory', {}, token),

  preKeyBundle: (token: string, deviceId: string) =>
    request<{
      deviceId: string;
      registrationId: number;
      identityKey: string;
      signedPreKey: { keyId: number; publicKey: string; signature: string };
      oneTimePreKey: { keyId: number; publicKey: string } | null;
    }>(`/v1/keys/${deviceId}`, {}, token),

  /**
   * Kurzlebige TURN-Zugangsdaten. Leere Liste heißt: kein TURN konfiguriert,
   * Anrufe funktionieren dann nur innerhalb desselben Netzes.
   */
  turnCredentials: (token: string) =>
    request<{ iceServers: RTCIceServer[]; expiresAt: number }>('/v1/turn', {}, token),

  /** Welche Funktionen dieser Server freigibt. */
  features: () =>
    request<{ openRegistration: boolean; adminConsole: boolean }>('/v1/features'),

  /**
   * Selbstregistrierung. Liefert dasselbe wie die Anmeldung, das Geraet wird
   * anschliessend wie gewohnt eingerichtet.
   */
  register: (email: string, displayName: string, password: string) =>
    request<{ accountId: string; handle: string; mustChangePassword: boolean; token: string }>(
      '/v1/register',
      { method: 'POST', body: JSON.stringify({ email, displayName, password }) },
    ),

  /** Ist Push auf diesem Server eingerichtet? */
  pushAvailable: (token: string) =>
    request<{ enabled: boolean; webPush: boolean; vapidPublicKey: string | null }>(
      '/v1/push',
      {},
      token,
    ),

  /**
   * Push-Token dieses Geraets hinterlegen — oder mit `null` entfernen.
   *
   * Die Geraete-ID kommt serverseitig aus dem Sitzungstoken, nicht aus dem
   * Rumpf: Ein Geraet kann nur seinen eigenen Token setzen.
   */
  registerPush: (token: string, pushToken: string | null, kind: 'fcm' | 'web' = 'fcm') =>
    request<void>(
      '/v1/push',
      { method: 'POST', body: JSON.stringify({ token: pushToken, kind }) },
      token,
    ),

  send: (token: string, envelopes: unknown[]) =>
    // `unknownDevices` nennt Ziele, die der Server nicht kennt oder deren
    // Konto gesperrt ist. Der Aufrufer MUSS das auswerten — der Server
    // antwortet auch dann mit 202, wenn nichts angenommen wurde.
    request<{ accepted: string[]; unknownDevices: string[] }>(
      '/v1/messages',
      { method: 'POST', body: JSON.stringify({ envelopes }) },
      token,
    ),

  receive: (token: string) =>
    request<{
      envelopes: Array<{
        messageId: string;
        sourceDeviceId: string | null;
        type: string;
        ciphertext: string;
        timestamp: number;
      }>;
    }>('/v1/messages', {}, token),

  /**
   * Zugestellte Nachrichten löschen. Wichtig, dass der Client das tut: Der
   * Server ist eine Warteschlange, kein Archiv.
   */
  acknowledge: (token: string, messageIds: string[]) =>
    request<void>(
      '/v1/messages',
      { method: 'DELETE', body: JSON.stringify({ messageIds }) },
      token,
    ),
};

/**
 * Benachrichtigung über neue Nachrichten. Der Kanal überträgt keine Nutzdaten,
 * sondern weckt nur — abgeholt wird über die normale HTTP-Route.
 */
export function openStream(token: string, onUpdate: () => void): () => void {
  // Ohne gesetzte API_BASE die eigene Herkunft — in der Android-App waere das
  // localhost, deshalb dort die konfigurierte Adresse.
  const base = API_BASE
    ? API_BASE.replace(/^http/, 'ws')
    : `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;

  // Das Token steht bewusst NICHT in der URL: Eine URL landet in Zugriffs- und
  // Fehlerprotokollen von Proxy und Webserver. Wer an diese Protokolle kommt,
  // haette damit gueltige Sitzungen. Der Nachrichtenrumpf taucht dort nicht
  // auf, deshalb wird erst verbunden und dann angemeldet.
  const socket = new WebSocket(`${base}/v1/stream`);

  socket.onopen = () => {
    socket.send(JSON.stringify({ type: 'auth', token }));
  };

  socket.onmessage = (event) => {
    const data = JSON.parse(event.data as string) as { type: string };
    // 'ready' bestaetigt die Anmeldung, 'ping' haelt die Verbindung offen —
    // beides braucht keine Reaktion. Nur 'queue_update' heisst, dass etwas
    // abzuholen ist.
    if (data.type === 'queue_update') onUpdate();
  };

  // Fallback, falls der Stream nicht zustande kommt — z. B. hinter einem
  // Proxy, der WebSockets blockiert. In Zensurumgebungen ist das der Normalfall.
  const poll = setInterval(onUpdate, 5000);

  return () => {
    clearInterval(poll);
    socket.close();
  };
}
